import { Button } from '@casumo/dojo-react';
import { Close } from '@casumo/dojo-react-icons';
import { memo, useCallback, useEffect } from 'react';
import { useDepositForm } from './hooks/useDepositForm';
import { useInitializeDeposit } from './hooks/useInitializeDeposit';
import { usePnpCashierContent, usePnpLimits } from './hooks/usePnpCashierData';
import { getCurrencySymbol } from './pnpCashier.utils';
import PnpCashierView from './PnpCashierView';
import { CasumoIcon, CasumoLoaderIcon } from '../../assets/icons';
import useMarketInfo from '../../hooks/useMarketInfo';
import useNavigateTo from '../../hooks/useNavigateTo';
import { useRegistrationStore } from '../../store/store';
import { TEST_ID } from '../../utils/constants';
import { MARKETS_CODES } from '../../utils/countries';
import { API_URL, IS_DEVELOPMENT, isTestEnvironment } from '../../utils/envConstants';
import { GtmEvents } from '../../utils/gtm/gtm.ts';
import { resetProgressInLocalStorage } from '../../utils/utils';
import { DEPOSIT_AMOUNT_KEY } from '../trustlyIframe/trustlyUtils';

type PnpCashierProps = {
  onSkip?: () => void;
};

const PnpCashier = ({ onSkip }: PnpCashierProps) => {
  const marketInfo = useMarketInfo();
  const { setMetadata } = useRegistrationStore();
  const { navigateToMarket } = useNavigateTo();

  const { content, isLoading: contentLoading } = usePnpCashierContent();
  const { limits, isLoading: limitsLoading } = usePnpLimits();

  const currency = limits?.currency ?? marketInfo.currency ?? 'EUR';
  const currencySymbol = getCurrencySymbol(currency, marketInfo.language);

  const {
    amount, error, handleAmountChange, handleSelectAmount, validate,
  } = useDepositForm(content, limits, currencySymbol);

  const { submit, isSubmitting } = useInitializeDeposit();

  const isLoading = contentLoading || limitsLoading;

  const handleAmountChangeWithSymbol = useCallback(
    (v: string) => handleAmountChange(v, currencySymbol),
    [handleAmountChange, currencySymbol],
  );

  const handleBlur = useCallback(() => validate(), [validate]);

  useEffect(() => {
    GtmEvents.logPnpStartScreen();
  }, []);

  const onClose = () => {
    resetProgressInLocalStorage(marketInfo.market);
    setMetadata({ isOpen: false });
    sessionStorage.setItem('fabric:registration:open', 'false');
    navigateToMarket(marketInfo.market);
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    sessionStorage.setItem(DEPOSIT_AMOUNT_KEY, amount);
    GtmEvents.logPnpTrustlyClickStart(Number(amount));

    const response = await submit({
      market:       marketInfo.marketCode ?? MARKETS_CODES.fi_fi,
      country:      marketInfo.countryCode,
      provider:     'trustly',
      jurisdiction: marketInfo.jurisdiction ?? '',
      amount:       {
        amount:              Number(amount),
        iso4217CurrencyCode: currency,
      },
      redirectUri: `${API_URL}/${marketInfo.market}/pay-and-play-callback`,

    });

    if (response) {
      setMetadata({ trustlyCheckoutUrl: response.url });
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center
        w-full h-full"
      >
        <div className="animate-spin rounded-md">
          <CasumoLoaderIcon width={64} height={64} />
        </div>
      </div>
    );
  }

  return (
    //TODO: Same header pattern as TrustlyHeader maybe we can extract a reusable component
    <div
      className="flex flex-col w-full self-stretch"
      style={{ flex: '1 1 0%', minHeight: 0 }}
      data-testid={TEST_ID.PREFIX + 'pnp-cashier'}
    >
      {/* Header — same pattern as TrustlyHeader */}
      <div
        className="flex items-center justify-between w-full
          px-2xs md:px-md h-3xl border-b-2 border-grey-0 md:h-[72px] overflow-hidden shrink-0
          bg-white"
      >
        <div className="w-2xl" />
        <div className="hidden sm:flex items-center justify-center
          gap-2xs w-full"
        >
          <span className="font-extrabold text-xl" data-testid={TEST_ID.PNP + 'cashier-header-title'}>
            {content.header}
          </span>
        </div>
        <a className="sm:hidden" href={`${URL}/${marketInfo?.market}`}>
          <CasumoIcon color="#4C00C2" />
        </a>
        <div
          className="flex items-center justify-end text-2xl
            mr-3xs w-2xl h-full md:invisible cursor-pointer
            text-purple-80"
          onClick={onClose}
          data-testid={TEST_ID.BUTTON_CLOSE}
        >
          <Close
            style={{ display: window?.native ? 'none' : 'flex' }}
          />
        </div>
      </div>

      {/* Content */}
      <div
        className="flex flex-col gap-sm h-full w-full
          p-sm md:px-lg lg:px-2xl md:pt-md lg:pt-lg overflow-y-auto animate-fadeIn"
        style={{ paddingBottom: 'var(--fixed-div-height)' }}
      >
        <PnpCashierView
          content={content}
          amount={amount}
          error={error}
          currencySymbol={currencySymbol}
          onAmountChange={handleAmountChangeWithSymbol}
          onSelectAmount={handleSelectAmount}
          onBlur={handleBlur}
        />
      </div>

      {/* Fixed bottom CTA */}
      <div
        className="fixed bottom-0 w-full py-lg px-md
          md:px-2xl pt-2xl animate-fadeIn pointer-events-none"
      >
        <Button
          className="pointer-events-auto"
          width="full"
          onClick={handleSubmit}
          disabled={isSubmitting}
          loading={isSubmitting}
          data-testid={TEST_ID.BUTTON_PRIMARY}
        >
          {content.ctaLabel}
        </Button>
        {(IS_DEVELOPMENT || isTestEnvironment()) && onSkip && (
          <button
            onClick={onSkip}
            className="pointer-events-auto block mx-auto mt-2xs
              text-grey-40 text-xs underline"
          >
            Skip (dev only)
          </button>
        )}
      </div>
    </div>
  );
};

const Memoized = memo(PnpCashier);
Memoized.displayName = 'PnpCashier';

export default Memoized;
